import { Box, Typography, Divider, Button, Snackbar, Alert } from "@mui/material";
import { useResponsive } from "../../../utils/mobileresponsive";
import { useState, useMemo, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { getAgentColumns } from "./Columns";
import TicketDetailsDialog from "../sharedcomponents/TicketDialog";
import TableFilters from "../sharedcomponents/TableFilters";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SnackbarNotification from "../../../components/ui/SnackBarNotification";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import AddTicketDialog from "./components/AddTicketDialog";
import useFetchAgentTickets from "./hooks/useFetchAgentTickets";
import useCreateTicket from "./hooks/useCreateTicket";
import useReplyConversation from "../hooks/useReplyConversation";
import dayjs from "dayjs";

const AgentSupport = () => {
	const { isMobile } = useResponsive();
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [filters, setFilters] = useState({
		search: "",
		category: "",
		status: "",
		date_from: "",
		date_to: "",
	});

	const { tickets, loading, refetch } = useFetchAgentTickets();
	const { createTicket, loading: createTicketLoading } = useCreateTicket();
	const { replyConversation, loading: replyLoading } = useReplyConversation();

	useEffect(() => {
		const handler = setTimeout(() => {
			setFilters((f) => ({
				...f,
				search: searchInput,
			}));
		}, 250);

		return () => clearTimeout(handler);
	}, [searchInput]);

	useEffect(() => {
		if (!selectedTicket) return;

		const updated = tickets.find((t) => t.id === selectedTicket.id);
		if (updated && updated !== selectedTicket) {
			setSelectedTicket(updated);
		}
	}, [tickets, selectedTicket]);

	const filteredRows = useMemo(() => {
		return tickets.filter((ticket) => {
			const search = filters.search.toLowerCase().trim();

			// ── SEARCH (ticketId)
			const matchesSearch = !search || ticket.ticketId?.toLowerCase().includes(search);

			// ── CATEGORY
			const matchesCategory = !filters.category || ticket.category === filters.category;

			// ── STATUS
			const matchesStatus = !filters.status || ticket.status === filters.status;

			// ── DATE RANGE (dateSubmitted)
			const matchesDateFrom = !filters.date_from || dayjs(ticket.dateSubmitted).isAfter(dayjs(filters.date_from).subtract(1, "day"));

			const matchesDateTo = !filters.date_to || dayjs(ticket.dateSubmitted).isBefore(dayjs(filters.date_to).add(1, "day"));

			return matchesSearch && matchesCategory && matchesStatus && matchesDateFrom && matchesDateTo;
		});
	}, [tickets, filters]);

	const handleViewDetails = (ticket) => {
		setSelectedTicket(ticket);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setSelectedTicket(null);
	};

	const handleSendReply = async (message) => {
		if (!selectedTicket) return;
		try {
			const result = await replyConversation({
				ticket_id: selectedTicket.id,
				role: "agent",
				message,
			});
			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to send reply. Please try again.", severity: "error" });
				return result;
			}
			setSnackbar({ open: true, message: "Reply sent succesfully." });
			refetch();
			return result;
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, message: "Unexpected error occurred.", severity: "error" });
		}
	};

	const handleCreateTicket = async (form) => {
		try {
			const result = await createTicket({
				subject: form.subject,
				description: form.description,
				category: form.category,
				role: "agent",
			});
			if (!result?.success) {
				setSnackbar({ open: true, message: result?.error || "Failed to create ticket.", severity: "error" });
				return result;
			}
			setSnackbar({ open: true, message: "Ticket created successfully." });
			refetch();
			return result;
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, message: "Unexpected error occurred.", severity: "error" });
		}
	};

	const columns = getAgentColumns(handleViewDetails);

	return (
		<Box p={4}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
				<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
					SUPPORT PAGE
				</Typography>
			</Box>
			<Box my={3}>
				<Divider />
			</Box>

			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3, mr: 2 }}>
				<Button
					variant="contained"
					startIcon={<SupportAgentIcon />}
					onClick={() => setAddDialogOpen(true)}
					sx={{
						px: 4,
						gap: 0.5,
						fontWeight: 700,
						backgroundColor: "#1D54A2 !important",
						...generateButtonSx({ bg: "#EBF5FF !important", text: "#1D54A2" }, { bg: "#EBF5FF !important", text: "#1D54A2" }),
					}}
				>
					Submit a Ticket
				</Button>
			</Box>

			<TableFilters filters={filters} setFilters={setFilters} searchTerm={searchInput} onSearchChange={setSearchInput} isAgent={true} />

			<DataGrid
				loading={loading}
				rows={filteredRows}
				columns={columns}
				disableColumnMenu
				disableColumnSorting
				disableColumnResize
				hideFooter
				unstable_heightStrategy="content"
				columnHeaderHeight={70}
				getRowHeight={() => "auto"}
				sx={{
					"& .MuiDataGrid-filler": { display: "none !important" },
					"& .MuiDataGrid-columnHeaders": { lineHeight: "normal" },
					"& .MuiDataGrid-columnHeaderTitle": {
						whiteSpace: "normal",
						lineHeight: "1.2",
						overflowWrap: "break-word",
						textAlign: "left",
						fontWeight: 700,
					},
					"& .MuiDataGrid-cell": { py: 1.5 },
				}}
			/>

			<TicketDetailsDialog
				open={dialogOpen}
				ticket={selectedTicket}
				onClose={handleCloseDialog}
				onSendReply={handleSendReply}
				isAgent={true}
				replyLoading={replyLoading}
			/>

			<AddTicketDialog
				open={addDialogOpen}
				onClose={() => setAddDialogOpen(false)}
				onSubmit={handleCreateTicket}
				loading={createTicketLoading}
			/>
			<SnackbarNotification
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			/>
		</Box>
	);
};
export default AgentSupport;
